
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useThemeColors } from "./hooks/useThemeColors";
import type { Candidate } from "@/components/candidates/types";

interface TimeToHireProps {
  candidates: Candidate[];
}

export const TimeToHire = ({ candidates }: TimeToHireProps) => {
  const { colors } = useThemeColors();

  const timeToHireData = useMemo(() => {
    if (!candidates || candidates.length === 0) {
      return [];
    }

    const hiredCandidates = candidates.filter(candidate => 
      candidate.status === 'hired' && 
      candidate.created_at && 
      candidate.accepted_date
    );

    if (hiredCandidates.length === 0) {
      return [];
    }

    // Calculate time to hire in days for each hired candidate
    const timeToHireValues = hiredCandidates.map(candidate => {
      const startDate = new Date(candidate.created_at!);
      const endDate = new Date(candidate.accepted_date!);
      const timeDiff = endDate.getTime() - startDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      return Math.max(0, daysDiff); // Ensure non-negative values
    });

    // Group by time ranges
    const ranges = [
      { range: "0-7 days", min: 0, max: 7 },
      { range: "8-14 days", min: 8, max: 14 },
      { range: "15-30 days", min: 15, max: 30 },
      { range: "31-60 days", min: 31, max: 60 },
      { range: "60+ days", min: 61, max: Infinity }
    ];

    return ranges.map(({ range, min, max }) => ({
      range,
      count: timeToHireValues.filter(days => days >= min && days <= max).length,
      percentage: Math.round((timeToHireValues.filter(days => days >= min && days <= max).length / timeToHireValues.length) * 100)
    })).filter(item => item.count > 0);
  }, [candidates]);

  const averageTimeToHire = useMemo(() => {
    const hiredCandidates = candidates.filter(candidate => 
      candidate.status === 'hired' && 
      candidate.created_at && 
      candidate.accepted_date
    );

    if (hiredCandidates.length === 0) return 0;

    const totalDays = hiredCandidates.reduce((sum, candidate) => {
      const startDate = new Date(candidate.created_at!);
      const endDate = new Date(candidate.accepted_date!);
      const timeDiff = endDate.getTime() - startDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      return sum + Math.max(0, daysDiff);
    }, 0);

    return Math.round(totalDays / hiredCandidates.length);
  }, [candidates]);

  if (!candidates || candidates.length === 0) {
    return (
      <Card className="overflow-hidden border-border">
        <CardHeader>
          <CardTitle className="text-foreground">Time to Hire</CardTitle>
          <CardDescription className="text-muted-foreground">No data available</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground bg-muted/20 rounded-lg border border-dashed border-border">
            Add hired candidates to see time to hire analytics
          </div>
        </CardContent>
      </Card>
    );
  }

  const hiredCount = candidates.filter(c => c.status === 'hired').length;

  if (hiredCount === 0) {
    return (
      <Card className="overflow-hidden border-border">
        <CardHeader>
          <CardTitle className="text-foreground">Time to Hire</CardTitle>
          <CardDescription className="text-muted-foreground">No hired candidates yet</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground bg-muted/20 rounded-lg border border-dashed border-border">
            Hire candidates to see time to hire analytics
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden border-border">
      <CardHeader>
        <CardTitle className="text-foreground">Time to Hire</CardTitle>
        <CardDescription className="text-muted-foreground">
          Average time to hire: {averageTimeToHire} days ({hiredCount} hires)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={timeToHireData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis 
                dataKey="range" 
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  color: 'hsl(var(--foreground))'
                }}
                formatter={(value: number, name: string) => [
                  `${value} candidates (${timeToHireData.find(d => d.count === value)?.percentage || 0}%)`,
                  'Count'
                ]}
              />
              <Bar dataKey="count" fill={colors.primary[0]} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
